import api, { ApiError } from './apiClient';
import type { ApiResponse } from 'shared/types/api';

export interface NotificationTemplate {
  type: string;
  name: string;
  email: string;
  sms: string;
  content?: string;
}

export interface NotificationTemplateResponse {
  success: boolean;
  data?: NotificationTemplate[];
  message?: string;
  error?: string;
}

export interface NotificationPreviewData {
  channel: 'email' | 'sms';
  recipient: string;
  message: string;
  missingCount: number;
  missingRequirements: Array<{
    name: string;
    status: string;
    isRequired: boolean;
  }>;
}

export interface NotificationPreviewResponse {
  success: boolean;
  data?: NotificationPreviewData;
  message?: string;
  error?: string;
}

export interface SendNotificationPayload {
  targetUserId: string;
  companyId: string;
  channel: 'email' | 'sms';
  customMessage?: string;
  onlyRequired?: boolean;
}

export interface SendNotificationData {
  notificationId: string;
  messageId: string;
  recipient: string;
  channel: 'email' | 'sms';
}

export interface SendNotificationResponse {
  success: boolean;
  message?: string;
  data?: SendNotificationData;
  error?: string;
  status?: number;
}

export interface BulkSendPayload {
  companyId: string;
  channel: 'email' | 'sms';
  roleFilter?: string;
  onlyRequired?: boolean;
}

export interface BulkSendResult {
  userId: string;
  status: 'exitoso' | 'fallido';
  recipient?: string;
  reason?: string;
}

export interface BulkSendResponse {
  success: boolean;
  message?: string;
  data?: {
    totalUsers: number;
    successCount: number;
    failureCount: number;
    results: BulkSendResult[];
  };
  error?: string;
}

class NotificationApiService {
  /**
   * Get notification templates by type
   */
  async getTemplates(type: string = 'verification'): Promise<NotificationTemplate[]> {
    try {
      const result = await api.get('/notifications/templates', { type });
      return result.data || [];
    } catch (error: unknown) {
      return [];
    }
  }

  /**
   * Get notification preview before sending
   */
  async getPreview(
    targetUserId: string,
    companyId: string,
    channel: 'email' | 'sms' = 'email',
    onlyRequired: boolean = true
  ): Promise<ApiResponse<NotificationPreviewData>> {
    try {
      const result = await api.get('/notifications/manual/preview', {
        targetUserId,
        companyId,
        channel,
        onlyRequired,
      });

      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error: unknown) {
      const apiErr = error as ApiError;
      const msg = apiErr?.error || (error instanceof Error ? error.message : 'Error al obtener vista previa');
      return {
        success: false,
        status: apiErr?.statusCode,
        message: msg,
        error: msg
      };
    }
  }

  /**
   * Send manual notification to a single user
   */
  async sendManualNotification(
    payload: SendNotificationPayload
  ): Promise<ApiResponse<SendNotificationData>> {
    try {
      const result = await api.post('/notifications/manual/send', payload);

      return {
        success: true,
        data: result.data,
        message: result.message || 'Notificación enviada exitosamente'
      };
    } catch (error: unknown) {
      const msg = (error as ApiError)?.error || (error instanceof Error ? error.message : 'Error al enviar notificación');
      return {
        success: false,
        message: msg,
        error: msg
      };
    }
  }

  /**
   * Send notifications to multiple users
   */
  async bulkSendNotifications(
    payload: BulkSendPayload
  ): Promise<ApiResponse<any>> {
    try {
      const result = await api.post('/notifications/manual/bulk-send', payload);

      return {
        success: true,
        data: result.data,
        message: result.message || 'Notificaciones enviadas exitosamente'
      };
    } catch (error: unknown) {
      const msg = (error as ApiError)?.error || (error instanceof Error ? error.message : 'Error al enviar notificaciones en lote');
      return {
        success: false,
        message: msg,
        error: msg
      };
    }
  }

  /**
   * Get user notifications with filters
   */
  async getNotifications(
    limit: number = 50,
    skip: number = 0,
    isRead?: boolean,
    type?: string
  ): Promise<ApiResponse<any>> {
    try {
      const params: Record<string, any> = { limit, skip };
      if (isRead !== undefined) params.isRead = isRead;
      if (type) params.type = type;

      const result = await api.get('/notifications', params);

      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error: unknown) {
      const msg = (error as ApiError)?.error || (error instanceof Error ? error.message : 'Error al obtener notificaciones');
      return {
        success: false,
        message: msg,
        error: msg
      };
    }
  }

  /**
   * Get notification by ID
   */
  async getNotificationById(notificationId: string): Promise<ApiResponse<any>> {
    try {
      const result = await api.get(`/notifications/${notificationId}`);

      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error: unknown) {
      const msg = (error as ApiError)?.error || (error instanceof Error ? error.message : 'Error al obtener notificación');
      return {
        success: false,
        message: msg,
        error: msg
      };
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string): Promise<ApiResponse<void>> {
    try {
      const result = await api.patch(`/notifications/${notificationId}/mark-read`);

      return {
        success: true,
        message: result.message || 'Notificación marcada como leída'
      };
    } catch (error: unknown) {
      const msg = (error as ApiError)?.error || (error instanceof Error ? error.message : 'Error al marcar como leída');
      return {
        success: false,
        message: msg,
        error: msg
      };
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<ApiResponse<void>> {
    try {
      const result = await api.patch('/notifications/mark-all-read');

      return {
        success: true,
        message: result.message || 'Notificaciones marcadas como leídas'
      };
    } catch (error: unknown) {
      const msg = (error as ApiError)?.error || (error instanceof Error ? error.message : 'Error al marcar como leídas');
      return {
        success: false,
        message: msg,
        error: msg
      };
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId: string): Promise<ApiResponse<void>> {
    try {
      await api.delete(`/notifications/${notificationId}`);

      return {
        success: true,
        message: 'Notificación eliminada exitosamente'
      };
    } catch (error: unknown) {
      const msg = (error as ApiError)?.error || (error instanceof Error ? error.message : 'Error al eliminar notificación');
      return {
        success: false,
        message: msg,
        error: msg
      };
    }
  }

  /**
   * Bulk delete notifications
   */
  async bulkDeleteNotifications(notificationIds: string[]): Promise<ApiResponse<void>> {
    try {
      await api.delete('/notifications/bulk-delete', { data: { notificationIds } });

      return {
        success: true,
        message: 'Notificaciones eliminadas exitosamente'
      };
    } catch (error: unknown) {
      const msg = (error as ApiError)?.error || (error instanceof Error ? error.message : 'Error al eliminar notificaciones');
      return {
        success: false,
        message: msg,
        error: msg
      };
    }
  }

  /**
   * Get notification statistics
   */
  async getStats(): Promise<ApiResponse<any>> {
    try {
      const result = await api.get('/notifications/stats');

      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error: unknown) {
      const msg = (error as ApiError)?.error || (error instanceof Error ? error.message : 'Error al obtener estadísticas');
      return {
        success: false,
        message: msg,
        error: msg
      };
    }
  }

  /**
   * Resend email notification
   */
  async resendEmailNotification(notificationId: string): Promise<ApiResponse<void>> {
    try {
      const result = await api.post(`/notifications/${notificationId}/resend-email`);

      return {
        success: true,
        message: result.message || 'Notificación reenviada exitosamente'
      };
    } catch (error: unknown) {
      const msg = (error as ApiError)?.error || (error instanceof Error ? error.message : 'Error al reenviar notificación');
      return {
        success: false,
        message: msg,
        error: msg
      };
    }
  }

  /**
   * Get notifications by company
   */
  async getNotificationsByCompany(
    companyId: string,
    limit: number = 50,
    skip: number = 0,
    type?: string,
    priority?: string
  ): Promise<ApiResponse<any>> {
    try {
      const params: Record<string, any> = { limit, skip };
      if (type) params.type = type;
      if (priority) params.priority = priority;

      const result = await api.get(`/notifications/company/${companyId}`, params);

      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error: unknown) {
      const msg = (error as ApiError)?.error || (error instanceof Error ? error.message : 'Error al obtener notificaciones de empresa');
      return {
        success: false,
        message: msg,
        error: msg
      };
    }
  }

  /**
   * Run manual expiration check
   */
  async runExpirationCheck(): Promise<ApiResponse<void>> {
    try {
      const result = await api.post('/notifications/run-expiration-check');

      return {
        success: true,
        message: result.message || 'Verificación ejecutada exitosamente'
      };
    } catch (error: unknown) {
      const msg = (error as ApiError)?.error || (error instanceof Error ? error.message : 'Error al ejecutar verificación');
      return {
        success: false,
        message: msg,
        error: msg
      };
    }
  }
}

export const notificationApi = new NotificationApiService();
